import React from 'react';

import { FormattedMessage, FormattedRelativeTime, useIntl } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import {
	isGoogleDocument,
	isGraphDocument,
	type SearchResultSlackMessage,
} from '@atlassian/dynamic-cards-search-client/graphql/third-party-legacy';

import { LastModified } from '../../../../../../../common/ui/last-modified';
import { RestrictedLozenge } from '../../../../../../../common/ui/restricted-lozenge';
import { SearchMetadata } from '../../../../../../../common/ui/search-metadata';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { messages } from '../../../../../../../common/ui/search-result/messages';
import { getIcon, getLabel } from '../../../../../../../common/utils/product-result-utils';
import {
	buildThreadDescription,
	buildThreadTitle,
	enrichExcerpt,
	getContributorNames,
	getTimeValues,
	getUniqueContributorsFromLinkedEntities,
	isNonPublic,
	processSlackMessage,
	timeSinceDate,
} from '../../../../../../../common/utils/search-result-utils';
import {
	useBootstrap,
	usePrimaryProduct,
	useSelectedProduct,
} from '../../../../../../../controllers/store';

const footerContainer = xcss({
	paddingTop: 'space.100',
});

interface ThirdPartySearchResultProps {
	partial: SearchResultSlackMessage;
	/** The position in the results list shown to the user. */
	position?: number;
}

export const SlackSearchResult = ({
	partial,
	position,
}: ThirdPartySearchResultProps): React.JSX.Element => {
	const [selectedProduct] = useSelectedProduct();
	const [primaryProduct] = usePrimaryProduct();
	const [{ cloudId, isAdminHubAIEnabled }] = useBootstrap();
	const intl = useIntl();
	const { formatMessage } = useIntl();
	const product = 'slack';
	const {
		lastModifiedDate,
		url,
		permissionLevel,
		mentions,
		type,
		subtype,
		linkedEntities,
		lastActive,
		allContributors,
	} = partial;
	let { description, title } = partial;

	let label = getLabel(partial);
	const lastModifiedComponent = lastModifiedDate ? (
		<LastModified lastModified={lastModifiedDate} />
	) : null;
	const [contributor] = (isGraphDocument(partial) && partial.initialContributors) || [];

	let parts: Array<string | [string, React.ReactNode]> = [
		label,
		['last-modified', lastModifiedComponent],
		contributor?.name,
	];

	let messageDescription;

	const latestModifiedDate = linkedEntities
		? linkedEntities[linkedEntities.length - 1]?.lastModifiedDate
		: lastModifiedDate;

	const timeValues = timeSinceDate(lastActive || latestModifiedDate || lastModifiedDate);
	const { unit, value } = getTimeValues(
		timeValues.seconds,
		timeValues.minutes,
		timeValues.hours,
		timeValues.days,
		timeValues.months,
		timeValues.years,
	);
	const relativeTime = (
		<FormattedRelativeTime value={-value} style="long" unit={unit} numeric="auto">
			{(formattedTime) => (
				<FormattedMessage {...messages['lastActive']} values={{ relativeDate: formattedTime }} />
			)}
		</FormattedRelativeTime>
	);

	const contributors = allContributors || [
		...getUniqueContributorsFromLinkedEntities([partial, ...linkedEntities]),
	];

	if (type === 'channel') {
		title = `#${title}`;
		messageDescription = '';
		// need to set these to empty strings to avoid showing any description in the search result
		description = '';

		parts = [
			formatMessage(messages.channelTypeLabel),
			formatMessage(messages.channelMemberCount, { memberCount: contributors.length }),
			['last-active', relativeTime],
		];
	} else {
		if (subtype === 'direct_message') {
			title = buildThreadTitle(
				contributors,
				formatMessage(messages.privateConversationText, {
					users: getContributorNames(contributors, intl),
				}),
				intl,
			);
			parts = [formatMessage(messages.messageTypeLabel), ['last-active', relativeTime]];
			messageDescription = enrichExcerpt(processSlackMessage(description, mentions));
		} else {
			// if it's a thread, build the title and description
			// otherwise if it's a message, just process the description
			if (linkedEntities?.length > 0) {
				title = buildThreadTitle(contributors, `#${title}`, intl);
				parts = [formatMessage(messages.threadTypeLabel), ['last-active', relativeTime]];
				messageDescription = buildThreadDescription(partial, intl);
			} else {
				title = buildThreadTitle(contributors, `#${title}`, intl);
				parts = [formatMessage(messages.messageTypeLabel), ['last-active', relativeTime]];
				messageDescription = buildThreadDescription(partial, intl);
			}
		}
	}

	const icon = getIcon(partial, selectedProduct, label);

	const isNotPublic = isNonPublic(permissionLevel);

	return (
		<SearchResult
			id={partial.id}
			description={messageDescription}
			icon={icon}
			headerMetadata={<SearchMetadata product={product} productSelected parts={parts} />}
			footer={
				isNotPublic && (
					<Box xcss={footerContainer}>
						<RestrictedLozenge restriction={permissionLevel} />
					</Box>
				)
			}
			title={enrichExcerpt(title)}
			url={url}
			isThirdParty
			// AI Summaries are only supported for Google Drive now
			showAISummary={isGoogleDocument(partial) && isAdminHubAIEnabled}
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={partial}
		/>
	);
};
